import { useCallback, useEffect, useRef } from "react";
import { twMerge } from "tailwind-merge";

import { MonitoringEventTypeType } from "../../shared/types/monitoring";
import { useAv, useAvPubsubMessaging } from "../../utils/av";

import { BaseFooter } from "./BaseFooter";
import { HandRaiseButton } from "./HandRaiseButton";
import { JoinInPersonClassButton } from "./JoinInPersonClassButton";
import { AIPanelToggleButton } from "./controls/AIPanelToggleButton";
import { ChatPanelToggleButton } from "./controls/ChatPanelToggleButton";
import { InstructorNotesPanelToggleButton } from "./controls/InstructorNotesPanelToggleButton";
import ToolsMenu from "./controls/ToolsMenu";
import { UserPanelToggleButton } from "./controls/UserPanelToggleButton";

import { withActiveAv } from "components/av/AvSubsystemLoader";
import { ScreenshareOptions } from "components/av/ScreenshareOptions";
import { useVirtualClassActions } from "components/av/virtual-class-utils";
import EndClassMenu from "components/footer/EndClassMenu";
import { PillBanner } from "components/footer/PillBanner";
import { ReactionFeed } from "components/footer/ReactionFeed";
import { EditModeButton } from "components/footer/controls/EditModeButton";
import { TimerBanner } from "components/footer/controls/TimerBanner";
import { PresentationOptions } from "components/materials/presentation/PresentationOptions";
import { useIsAssessing } from "components/materials/section/assessment/hooks";
import { FooterOptionsMenu } from "components/menus/FooterOptionsMenu";
import { ReactionMenu } from "components/menus/ReactionMenu";
import { t } from "i18n/i18n";
import { IconText } from "mds/components/IconText";
import { PillButton } from "mds/components/PillButton";
import { Tooltip } from "mds/components/Tooltip";
import { useIsLgOrLarger, useIsMdOrLarger } from "mds/hooks/use-responsive";
import { VideoIcon } from "mds/icons";
import { useCapabilities } from "store/capability-hooks";
import { useAppDispatch, useAppSelector } from "store/index";
import {
  selectCurrentClassUserIsInPersonClass,
  selectCurrentSectionUserId,
  selectIsClassSessionInPerson,
} from "store/selectors";
import { selectIsInBreakout } from "store/selectors/breakouts";
import { selectIsObservingVirtualClass } from "store/selectors/pubsub-virtual-class-state";
import { selectScreensharingClassUserId } from "store/selectors/virtual-classes";
import { viewStateActions } from "store/slices/view";
import { useCreateMonitoringEvent } from "utils/hooks/use-create-monitoring-event";
import { FeatureStatus, usePresentation } from "utils/presentation";

const LazyVideoMicControls = withActiveAv(() => import("components/av/VideoMicControls"));
const LazyBreakoutsBanner = withActiveAv(
  () => import("components/footer/controls/BreakoutsBanner"),
);

export const InstructorMainFooter = () => {
  const isLargeScreen = useIsLgOrLarger();
  const isMdScreen = useIsMdOrLarger();
  const isAssessing = useIsAssessing();
  const screensharingClassUserId = useAppSelector(selectScreensharingClassUserId);
  const isInBreakout = useAppSelector(selectIsInBreakout);
  const { canObserveVirtualClass } = useCapabilities();
  const isClassSessionInPerson = useAppSelector(selectIsClassSessionInPerson);
  const currentSectionUserId = useAppSelector(selectCurrentSectionUserId);
  const {
    startObservingVirtualClass,
    stopObservingVirtualClass,
    showAvCheckToJoinVirtualClass,
    stopVirtualClass,
  } = useVirtualClassActions();
  const { isVirtualClassActive, isInVirtualClass, isAvActiveInAnotherTab, isAvSupportedInBrowser } =
    useAv();
  const isInInPersonClass = useAppSelector(selectCurrentClassUserIsInPersonClass);
  const isInClassSession = isInVirtualClass || isInInPersonClass;

  const controlsRef = useRef<HTMLDivElement>(null);
  const { featureStatus, isPresentingUser } = usePresentation();
  const dispatch = useAppDispatch();
  const isFeaturing = featureStatus !== FeatureStatus.NONE;
  const { sendPubsubVirtualClassStateMessage } = useAvPubsubMessaging();
  const isObservingClass = useAppSelector(selectIsObservingVirtualClass) && isVirtualClassActive;
  // The user can see observing controls if they have the capability and are not enrolled
  const shouldShowObservingControls = canObserveVirtualClass && !currentSectionUserId;

  const onJoinVirtualClass = useCallback(() => {
    showAvCheckToJoinVirtualClass();
  }, [showAvCheckToJoinVirtualClass]);

  const onMoveVirtualClassToThisTab = useCallback(() => {
    sendPubsubVirtualClassStateMessage({
      action: "join",
      force: true,
    });
  }, [sendPubsubVirtualClassStateMessage]);

  const onStartObservingVirtualClass = useCallback(() => {
    startObservingVirtualClass();
  }, [startObservingVirtualClass]);

  const onStopObservingVirtualClass = useCallback(() => {
    stopObservingVirtualClass();
  }, [stopObservingVirtualClass]);

  const joinButton = (
    <PillButton
      eventName="Join Class"
      kind="blue"
      size="s"
      title={t("footer.join_session")}
      onClick={onJoinVirtualClass}
    >
      {t("footer.join_session")}
    </PillButton>
  );

  const observeButton = (
    <Tooltip text={t("av.can_only_observe")}>
      <PillButton
        eventName="Join Class as Observer"
        kind="blue"
        size="s"
        title={t("av.observe_class")}
        onClick={onStartObservingVirtualClass}
      >
        {t("av.observe_class")}
      </PillButton>
    </Tooltip>
  );

  const stopObservingButton = (
    <PillButton
      eventName="Stop Observing Class"
      kind="blue"
      size="s"
      title={t("av.stop_observing_class")}
      onClick={onStopObservingVirtualClass}
    >
      {t("av.stop_observing_class")}
    </PillButton>
  );

  const endButton = (
    <PillButton
      className="border border-red-tint-70 bg-red-tint-99"
      disabled={shouldShowObservingControls}
      eventName="End Class for All"
      size="s"
      title={t("av.stop_virtual_class_short")}
      onClick={stopVirtualClass}
    >
      {t("av.stop_virtual_class_short")}
    </PillButton>
  );

  // Define the final buttons with conditional tooltip wrapping
  const joinOrObserveButton = isObservingClass
    ? stopObservingButton
    : shouldShowObservingControls
      ? observeButton
      : joinButton;

  const endButtonWithTooltip = shouldShowObservingControls ? null : endButton;

  // Prevent the feature modal from closing when clicking on the footer
  useEffect(() => {
    const keepFooterOpenOnMouseDown = () => {
      dispatch(viewStateActions.setPreventFeatureModalClosing(true));
    };

    const controls = controlsRef.current;

    if (isPresentingUser && isFeaturing && controls) {
      controls.addEventListener("mousedown", keepFooterOpenOnMouseDown);
    }

    return () => {
      controls?.removeEventListener("mousedown", keepFooterOpenOnMouseDown);
    };
  }, [isPresentingUser, isFeaturing, dispatch]);

  const createMonitoringEvent = useCreateMonitoringEvent();
  useEffect(() => {
    if (isVirtualClassActive && isAvActiveInAnotherTab) {
      createMonitoringEvent(MonitoringEventTypeType.AV_PRESENT_IN_ANOTHER_TAB, {});
    }
  }, [isVirtualClassActive, isAvActiveInAnotherTab, createMonitoringEvent]);

  const MaybeJoinVirtualClassButton =
    isVirtualClassActive && !isInVirtualClass && isAvSupportedInBrowser ? (
      <PillBanner
        buttonChildren={
          isAvActiveInAnotherTab ? (
            <PillButton
              eventName="Move Virtual Class to This Tab"
              kind="blue"
              size="s"
              title={t("av.move_virtual_class_to_this_tab")}
              onClick={onMoveVirtualClassToThisTab}
            >
              {t("av.move_virtual_class_to_this_tab")}
            </PillButton>
          ) : (
            <>
              {joinOrObserveButton}
              {endButtonWithTooltip}
            </>
          )
        }
        kind="blue"
        textChildren={
          <IconText
            iconStart={<VideoIcon className="text-blue-shade-40" />}
            text={
              isAvActiveInAnotherTab
                ? t("av.virtual_class_in_another_tab")
                : t("av.virtual_class_in_session")
            }
            truncateText
          />
        }
      />
    ) : null;

  const MaybeJoinInPersonClassButton =
    isClassSessionInPerson && !isInInPersonClass ? <JoinInPersonClassButton isInstructor /> : null;
  const canViewClass = isInClassSession || isObservingClass;

  return (
    <BaseFooter darkMode={canViewClass}>
      {isInClassSession && <ReactionFeed />}
      <div
        className={twMerge(
          "flex h-full w-full items-center",
          isInClassSession ? "justify-between" : "justify-center",
        )}
      >
        <LazyVideoMicControls />

        <div className="flex h-full w-full items-center justify-center gap-2">
          <div className="flex h-full min-w-0 items-center justify-end gap-2" ref={controlsRef}>
            {MaybeJoinVirtualClassButton}
            {MaybeJoinInPersonClassButton}
            {screensharingClassUserId ? (
              <ScreenshareOptions />
            ) : canViewClass ? (
              <PresentationOptions />
            ) : null}

            {isInClassSession && <ToolsMenu />}

            {isMdScreen && <EditModeButton isDisabled={isAssessing} />}

            {isVirtualClassActive && isInVirtualClass && isInBreakout && <LazyBreakoutsBanner />}
            {isInClassSession && <TimerBanner />}
          </div>

          {isVirtualClassActive && isInVirtualClass && <HandRaiseButton />}

          {isInClassSession && (
            <>
              <ReactionMenu />
              <ChatPanelToggleButton isLarge />
            </>
          )}

          {isLargeScreen ? (
            <>
              <UserPanelToggleButton />
              <InstructorNotesPanelToggleButton />
              <AIPanelToggleButton />
            </>
          ) : (
            <FooterOptionsMenu />
          )}
        </div>

        {isInClassSession && <EndClassMenu />}
      </div>
    </BaseFooter>
  );
};
